<script>
import { user } from '@constants/URL'
import { toMoney, toPercent } from '@utils/Format'
import { getCurrencyByInvestmentType } from '@modules/consolidator/utils/InvestmentType/InvestmentType.js'
import { resolveAssetGroupKey } from '@modules/consolidator/utils/resolve-asset-group-key/resolve-asset-group-key'
import {
  ASSET_TAGS,
  WALLET_EMPTY_MESSAGE,
} from '@modules/consolidator/constants/Text'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import AssetsByGroupTable from '@modules/consolidator/components/assets-by-group-table/assets-by-group-table.vue'
import ActionButton from '@modules/back-office/components/action-button/action-button.vue'
import SelectGroupedSearch from '@modules/global/components/select-grouped-search/select-grouped-search.vue'
import {
  buildColumnGroups,
  LOCKED_COLUMN_KEYS,
  WALLET_ASSET_COLUMN_CONFIG,
} from '@modules/consolidator/constants/wallet-asset-columns'
import { podeCurarSugestoesIa } from '@modules/consolidator/utils/suggestions-acesso/suggestions-acesso'
import {
  getStoredColumnKeys,
  migrateStoredColumnsForAnbima,
  setStoredColumnKeys,
  ensureLockedColumns,
  columnKeysToSelectorIds,
} from '@modules/consolidator/utils/wallet-columns-preferences/wallet-columns-preferences'

export default {
  name: 'CarteiraAtivos',
  components: {
    FeedbackWrapper,
    AssetsByGroupTable,
    ActionButton,
    SelectGroupedSearch,
  },
  props: {
    // A fila e carregada pela VIEW (que conhece o cliente antes de a carteira responder) e desce
    // como prop — antes a tabela buscava sozinha e a requisicao mais leve saia ~17s depois.
    sugestoesPendentesPorAtivo: { type: Object, default: null },
    sugestoesPorMovimentacao: { type: Object, default: null },
    movimentacoesDasSugestoes: { type: Object, default: null },
    carregandoSugestoes: { type: Boolean, default: false },
    statisticsAssets: { type: Object, default: () => ({}) },
    clienteAtual: { type: Object, default: () => ({}) },
    ativosSelecionados: { type: Array, default: () => [] },
    carteira: { type: Object, default: () => ({}) },
    groupBy: { type: String, default: 'subclasse' },
    modoFiltroCorretora: { type: String, default: '' },
    loadingRentAsset: { type: Boolean, default: false },
    estatisticasClasse: { type: Object, default: () => ({}) },
    composicaoFundos: { type: Object, default: () => ({}) },
    movimentacoes: { type: Object, default: () => ({}) },
    alertMarcacaoMercadoRF: { type: Object, default: () => ({}) },
    mapperBenchmarks: { type: Object, default: () => ({}) },
    filtroCorretora: { type: Array, default: () => [] },
    dataFim: { type: String, default: '' },
    benchmarks: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    reloading: { type: Boolean, default: false },
    investmentType: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    // Allowlist de acoes por chave — ver o mesmo prop na tabela interna.
    allowedActions: { type: Array, default: null },
    // Catalogo de colunas INJETAVEL. Default = o da carteira do cliente, entao
    // quem nao passa nada segue igual; a carteira do grupo passa o seu, que e o
    // mesmo mais a coluna `membro`.
    columnsConfig: { type: Array, default: () => WALLET_ASSET_COLUMN_CONFIG },
    visibleColumns: { type: Array, default: () => [] },
    carteirasRecomendadas: { type: Array, default: () => [] },
    groupLabelByKey: { type: Object, default: null },
    plTotal: { type: Number, default: null },
  },
  data() {
    return {
      loadingInternal: false,
      showOldAssets: false,
      searchQuery: '',
      searchInput: '',
      searchTimer: null,
      WALLET_EMPTY_MESSAGE,
      mostrarClasseAnbima: false,
      professionalColumnKeys: [],
    }
  },
  computed: {
    textsAssetTags() {
      return ASSET_TAGS
    },
    mostraLegendaSugestoes() {
      return podeCurarSugestoesIa({
        user: this.$currentUser(),
        access: this.$access,
      })
    },
    showLoadingState() {
      return this.loading || this.reloading
    },
    effectiveVisibleColumns() {
      if (this.$currentUser().IS_CLIENTE) return this.visibleColumns
      return this.professionalColumnKeys
    },
    columnGroups() {
      return buildColumnGroups(this.columnsConfig)
    },
    lockedSelectorIds() {
      return columnKeysToSelectorIds(LOCKED_COLUMN_KEYS)
    },
    // O id de uma coluna-folha no seletor e o ROTULO dela. Os mapas globais
    // (`columnKeysToSelectorIds`) so conhecem o catalogo do cliente, entao uma
    // coluna injetada voltava como a propria chave (`membro`), nao casava com a
    // opcao (`Membro`) e o seletor a emitia como DESMARCADA — apagando a coluna
    // da preferencia logo depois do `created`. Por isso os mapas saem do
    // catalogo EM USO.
    mapaChaveParaRotulo() {
      const mapa = {}
      for (const column of this.columnsConfig) {
        if (!column.group) mapa[column.key] = column.label
      }
      return mapa
    },
    mapaRotuloParaChave() {
      const mapa = {}
      for (const key of Object.keys(this.mapaChaveParaRotulo)) {
        mapa[this.mapaChaveParaRotulo[key]] = key
      }
      return mapa
    },
    selectedColumnIds() {
      return this.professionalColumnKeys.map(
        (key) => this.mapaChaveParaRotulo[key] || key
      )
    },
    ativosSelecionadosInside: {
      get() {
        return this.ativosSelecionados || []
      },
      set(newVal) {
        this.$emit('updateAtivosSelecionados', newVal)
      },
    },
    assets() {
      if (!this.carteira) return []
      const all = Object.values(this.carteira)
        .map((i) => i.ativos)
        .flat()
      if (this.showOldAssets) return all
      return all.filter((asset) => asset && asset['Aplicação'] !== 0)
    },
    // Lista PLANA dos ativos da carteira — a unica que carrega o NOME. A view precisa dela para
    // nomear a sugestao de cadastro no painel (a sugestao so traz a PK) e para resolver o alvo do
    // "Editar e aceitar"; `consolidator/fullWallet` nao serve aqui, vem vazio nesta tela.
    //
    // Sai da PROP, nao do `tableWallet`: aquele passa por `assets`, que esconde ativo RESGATADO
    // (`Aplicação === 0`) atras do toggle "Apresentar ativos resgatados" e ainda aplica o filtro de
    // corretora. A sugestao de um ativo resgatado continua pendente — contagem de curadoria nao
    // pode virar refem de opcao de exibicao.
    ativosPlanos() {
      return Object.values(this.carteira || {})
        .flatMap((grupo) => (grupo && grupo.ativos) || [])
        .filter(Boolean)
    },
    tableWallet() {
      const grouped = this.groupAssets()
      this.normalizeGroups(grouped)

      if (!this.filtroCorretora || this.filtroCorretora.length === 0) {
        Object.keys(grouped).forEach((k) => {
          grouped[k].carteira = grouped[k].ativos
        })
        return grouped
      }

      return this.applyBrokerFilter(grouped)
    },
    selectedAssetsByClass() {
      const map = {}
      this.ativosSelecionadosInside.forEach((assetId) => {
        const found = this.assets.find((a) => {
          return a && a.id === assetId
        })
        if (!found) {
          return
        }
        const key = resolveAssetGroupKey(
          found,
          this.groupBy,
          this.groupLabelByKey
        )
        if (!map[key]) {
          map[key] = []
        }
        map[key].push(found)
      })
      return map
    },
    filterOldAssets: {
      get() {
        return this.showOldAssets
      },
      set(val) {
        this.loadingInternal = true
        this.showOldAssets = val
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadingInternal = false
          }, 100)
        })
      },
    },
    // As colunas ligadas por padrão saem do catálogo EM USO, não do global:
    // senão uma coluna injetada nasce invisível.
    colunasPadraoDoCatalogo() {
      return this.columnsConfig
        .filter((column) => column.defaultOn)
        .map((column) => column.key)
    },
    chavesExclusivasDoCatalogo() {
      const doCliente = new Set(
        WALLET_ASSET_COLUMN_CONFIG.map((column) => column.key)
      )
      return this.columnsConfig
        .filter((column) => column.defaultOn && !doCliente.has(column.key))
        .map((column) => column.key)
    },
    currency() {
      return getCurrencyByInvestmentType({
        investmentType: this.investmentType,
      })
    },
  },
  watch: {
    columnsConfig() {
      this.professionalColumnKeys = this.resolveInitialColumnKeys()
    },
    searchInput() {
      this.scheduleSearchUpdate()
    },
    // `immediate` porque a carteira pode chegar antes do primeiro watch: sem ele a view fica com a
    // lista vazia ate o proximo recalculo, e o painel volta a abrir sem o nome do ativo.
    ativosPlanos: {
      immediate: true,
      handler(lista) {
        this.$emit('ativos-planos', lista || [])
      },
    },
  },
  created() {
    this.professionalColumnKeys = this.resolveInitialColumnKeys()
  },
  beforeDestroy() {
    this.clearSearchTimer()
  },
  methods: {
    scheduleSearchUpdate() {
      this.clearSearchTimer()
      this.searchTimer = setTimeout(() => {
        this.applySearchUpdate()
      }, 300)
    },
    applySearchUpdate() {
      this.searchQuery = (this.searchInput || '').trim()
    },
    clearSearchTimer() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
    },
    groupAssets() {
      const grouped = {}
      let totalPl = 0
      this.assets.forEach((asset) => {
        totalPl += asset['Aplicação'] || 0

        const key = resolveAssetGroupKey(
          asset,
          this.groupBy,
          this.groupLabelByKey
        )
        if (!key) return

        if (!grouped[key]) {
          grouped[key] = {
            ativos: [],
            Aplicação: 0,
            nome: key,
          }
        }

        grouped[key].ativos.push(asset)
        grouped[key]['Aplicação'] += asset['Aplicação'] || 0
      })

      grouped.__totalPl = totalPl
      return grouped
    },
    normalizeGroups(grouped) {
      const totalPl = grouped.__totalPl || 0
      delete grouped.__totalPl

      Object.values(grouped).forEach((group) => {
        group['ROA'] = '-'
        group.final = totalPl
          ? toPercent((group['Aplicação'] / totalPl) * 100)
          : ''
        group['Aplicação'] = this.formatMoney(group['Aplicação'])
      })
    },
    applyBrokerFilter(grouped) {
      const filtered = { ...grouped }
      Object.keys(filtered).forEach((k) => {
        filtered[k].carteira = filtered[k].ativos.filter((a) => {
          return this.filtroCorretora.includes(
            resolveAssetGroupKey(a, this.modoFiltroCorretora)
          )
        })
      })
      Object.keys(filtered).forEach((k) => {
        if (!filtered[k].carteira.length) {
          delete filtered[k]
        }
      })
      return filtered
    },
    modalEntendaCarteira() {
      this.$showModal({
        title: 'Posição do seu cliente',
        html: `
          <p style='text-align: justify; margin-bottom: 10px'>
           Conseguimos atualizar automaticamente a posição do seu cliente nos bancos e corretoras. Para algumas instituições/produtos, entretanto, você ou seu cliente precisam cadastrar a data e o valor da aplicação inicial para conseguirmos cotizar a carteira. Na aba movimentações, no topo dessa tela, você consegue adicionar eventuais aportes/resgates de forma bastante simples também. Caso precise de importações/integrações extras, entre em contato com nossa equipe.
          </p>
          <a href="${user.suporte}" rel="noopener noreferrer" target="_blank" style="text-align: center">
            Para mais informações acesse nossa FAQ.
          </a>
        `,
        'max-width': '700px',
        buttons: [
          {
            text: 'Ok',
            class: { primary: true },
          },
        ],
      })
    },
    selecionarAtivosClasse(carteira, $event) {
      this.$emit('selecionarAtivosClasse', carteira, $event)
    },
    openSaveBenchmark(classe) {
      this.$emit('openSaveBenchmark', classe)
    },
    openNewAtivo(ativo) {
      this.$emit('openNewAtivo', ativo)
    },
    aporteResgate(ativo, modo) {
      this.$emit('aporteResgate', ativo, modo)
    },
    openVincular(origem) {
      this.$emit('openVincular', origem)
    },
    openModalDelete(ativo) {
      this.$emit('openModalDelete', ativo)
    },
    formatMoney(v) {
      return toMoney(v, 2, this.currency)
    },
    resolveInitialColumnKeys() {
      // ★ Com catálogo INJETADO o conjunto é do catálogo, ponto: a preferência
      // salva é da carteira do CLIENTE, foi feita sobre outro conjunto de
      // colunas e não tem por que mandar aqui. Vale também na direção inversa —
      // o que o consumidor injetado mostra não contamina a tela do cliente.
      if (this.chavesExclusivasDoCatalogo.length > 0) {
        return ensureLockedColumns(this.colunasPadraoDoCatalogo.slice())
      }
      const stored = getStoredColumnKeys()
      if (stored !== null) {
        return ensureLockedColumns(migrateStoredColumnsForAnbima(stored))
      }
      const initial = this.colunasPadraoDoCatalogo.slice()
      const legacyClasseAnbima =
        localStorage.getItem('consolidator:show_classe_anbima') === 'true'
      if (legacyClasseAnbima && !initial.includes('classe_anbima')) {
        initial.push('classe_anbima')
      }
      return ensureLockedColumns(initial)
    },
    handleColumnsInput(selectorIds) {
      const keys = (selectorIds || []).map(
        (id) => this.mapaRotuloParaChave[id] || id
      )
      this.applyColumnPreferences(keys)
    },
    resetColumnsToDefault() {
      this.applyColumnPreferences(this.colunasPadraoDoCatalogo.slice())
    },
    applyColumnPreferences(columnKeys) {
      const withLocked = ensureLockedColumns(columnKeys)
      this.professionalColumnKeys = withLocked
      // A preferencia e COMPARTILHADA com a carteira do cliente. Gravar dali uma
      // coluna que so existe no catalogo injetado contamina a outra tela, entao
      // quem injeta catalogo proprio nao persiste.
      if (this.chavesExclusivasDoCatalogo.length === 0) {
        setStoredColumnKeys(withLocked)
      }
    },
  },
}
</script>

<template>
  <div
    class="carteira-ativos az-card-bordered"
    :class="{ 'carteira-ativos--loading': showLoadingState }"
  >
    <FeedbackWrapper
      :loading="showLoadingState"
      :message="WALLET_EMPTY_MESSAGE"
      :minHeight="425"
    >
      <div class="carteira-ativos__header">
        <div class="carteira-ativos__title">
          <span class="carteira-ativos__title-text az-ds-card-title">
            Carteira
            {{
              statisticsAssets.Todos && dataFim
                ? statisticsAssets.Todos.data_fim
                : 'atual'
            }}
            {{
              clienteAtual.name
                ? ' - ' + (clienteAtual.name.split(' ')[0] || '')
                : ''
            }}
          </span>
          <v-btn
            v-if="!$currentUser().IS_CLIENTE"
            icon
            x-small
            dense
            @click="modalEntendaCarteira"
          >
            <icon-svg name="mdi-information" size="16" color="#8E8E8E" />
          </v-btn>
        </div>

        <div class="d-flex align-center" style="gap: 12px">
          <v-checkbox
            v-model="filterOldAssets"
            label="Apresentar ativos resgatados"
            hide-details
            class="m-0 p-0"
          />

          <input-by-type
            v-model="searchInput"
            type="search"
            placeholder="Pesquisar ativo"
          />
        </div>
      </div>

      <div
        v-if="loadingInternal || carregandoSugestoes"
        class="carteira-ativos__loading"
      >
        <v-progress-linear indeterminate color="primary" rounded />
      </div>

      <AssetsByGroupTable
        :sugestoes-pendentes-por-ativo="sugestoesPendentesPorAtivo"
        :sugestoes-por-movimentacao="sugestoesPorMovimentacao"
        :movimentacoes-das-sugestoes="movimentacoesDasSugestoes"
        :statisticsAssets="statisticsAssets"
        :clienteAtual="clienteAtual"
        :ativosSelecionados="ativosSelecionadosInside"
        :ativosSelecionadosPorClasse="selectedAssetsByClass"
        :carteiraTabela="tableWallet"
        :estatisticasClasse="estatisticasClasse"
        :composicaoFundos="composicaoFundos"
        :movimentacoes="movimentacoes"
        :alertMarcacaoMercadoRF="alertMarcacaoMercadoRF"
        :mapperBenchmarks="mapperBenchmarks"
        :filtroCorretora="filtroCorretora"
        :modoFiltroCorretora="modoFiltroCorretora"
        :loadingRentAsset="loadingRentAsset"
        :benchmarks="benchmarks"
        :ativos="assets"
        :search-query="searchQuery"
        :currency="currency"
        :investmentType="investmentType"
        :readonly="readonly"
        :allowed-actions="allowedActions"
        :columns-config="columnsConfig"
        :visible-columns="effectiveVisibleColumns"
        :carteirasRecomendadas="carteirasRecomendadas"
        :mostrarClasseAnbima="mostrarClasseAnbima"
        :plTotal="plTotal"
        @updateAtivosSelecionados="(val) => (ativosSelecionadosInside = val)"
        @selecionarAtivosClasse="selecionarAtivosClasse"
        @openSaveBenchmark="openSaveBenchmark"
        @openNewAtivo="openNewAtivo"
        @aporteResgate="aporteResgate"
        @openVincular="openVincular"
        @openModalDelete="openModalDelete"
      >
        <template v-slot:actions-header>
          <SelectGroupedSearch
            v-if="!$currentUser().IS_CLIENTE"
            class="carteira-ativos__columns-selector"
            :items="columnGroups"
            :value="selectedColumnIds"
            :disabled-items="lockedSelectorIds"
            :menu-min-width="325"
            selectable-leaf-groups
            preserve-order
            hide-clear-selection
            @input="handleColumnsInput"
          >
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <ActionButton
                  iconOnly
                  iconName="colunas-3"
                  label="Colunas visíveis"
                />
              </span>
            </template>
            <template v-slot:footer>
              <BaseButton
                underlined
                sm
                fitContent
                @click="resetColumnsToDefault"
              >
                Restaurar padrão
              </BaseButton>
            </template>
          </SelectGroupedSearch>
        </template>
      </AssetsByGroupTable>

      <div class="carteira-ativos__legenda">
        <div class="carteira-ativos__legenda-title text-subtitle-small mb-2">
          LEGENDA
        </div>
        <div class="carteira-ativos__legenda-items text-xs text-color-900">
          <div
            v-if="mostraLegendaSugestoes"
            class="d-inline-flex align-center az-gap-xsmall"
            data-test="legenda-sugestoes-ia"
          >
            <!-- Legenda EXPLICA o símbolo; não age. Por isso o selo aqui é uma cópia estática
                 (como o "MM" e o "isento de IR" logo abaixo), e não o componente interativo da
                 linha: com ele, a legenda mostrava uma contagem fixa e um "Clique para revisar"
                 que não revisa nada. -->
            <span class="carteira-ativos__legenda-sugestoes">
              <icon-svg name="warning" size="10" color="var(--warning-new)" />
            </span>
            <span> {{ textsAssetTags.legendaSugestoes }} </span>
          </div>

          <div class="d-inline-flex align-center az-gap-xsmall">
            <v-chip style="height: 16px" color="info" x-small> MM </v-chip>
            <span> Marcação a mercado </span>
          </div>

          <div class="d-inline-flex align-center az-gap-xsmall">
            <v-chip style="height: 16px" color="primary" x-small>
              <v-icon small> mdi-currency-usd-off </v-icon>
            </v-chip>
            <span> Isento de IR </span>
          </div>

          <div class="d-inline-flex align-center az-gap-xsmall">
            <v-chip
              style="height: 16px; text-decoration: line-through"
              color="success"
              x-small
            >
              fee
            </v-chip>
            <span> Isento no cálculo de fee </span>
          </div>

          <div class="d-inline-flex align-center az-gap-xsmall">
            <v-chip
              style="height: 16px; text-decoration: line-through"
              color="warning"
              x-small
            >
              %
            </v-chip>
            <span> Desconsiderado na rentabilidade </span>
          </div>

          <div class="d-inline-flex align-center az-gap-xsmall">
            <icon-svg name="mdi-chart-pie" size="16" color="primary" />
            <span> Ativo com composição </span>
          </div>

          <div class="d-inline-flex align-center az-gap-xsmall">
            <icon-svg name="mdi-domain" size="16" color="info" />
            <span> Ativo iliquido </span>
          </div>

          <div
            v-if="!$currentUser().IS_CLIENTE"
            class="d-inline-flex align-center az-gap-xsmall"
          >
            <icon-svg name="warning-status" color="#E0AF00" size="16" />
            <span> Fora da margem suitability </span>
          </div>
        </div>
      </div>
    </FeedbackWrapper>
  </div>
</template>

<style lang="sass">

.carteira-ativos
  padding-bottom: 32px

  &--loading
    padding: 0 !important

  &__columns-selector
    display: inline-flex

  &__header
    display: flex
    align-items: flex-start
    justify-content: space-between
    margin-bottom: 17px

  &__title
    display: flex
    gap: 4px
    align-items: center
    justify-content: flex-start

  &__loading
    display: flex
    justify-content: center
    align-items: center
    margin-bottom: 20px

  &__legenda
    margin-top: 24px

    &-items
      display: flex
      gap: 24px
      flex-wrap: wrap

.carteira-ativos__legenda-sugestoes
  display: inline-flex
  align-items: center
  justify-content: center
  height: 18px
  padding: 0 8px
  border-radius: 16px
  // Mesmo token do selo da linha (`--warning-new`), pelo mesmo color-mix — sem hex duplicado.
  background: color-mix(in srgb, var(--warning-new) 20%, white)
</style>
